import { t } from 'i18next';

import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { formatUtils } from '@/lib/utils';
import { AdminAccountsOverview } from '@activepieces/shared';

import {
  buildOverviewKpis,
  OverviewKpi,
  OverviewKpiKey,
} from './overview-kpis';

const KPI_LABELS: Record<OverviewKpiKey, () => string> = {
  activationRate: () => t('Activation rate'),
  activeAccounts: () => t('Active accounts (30d)'),
  provisionedAccounts: () => t('Provisioned accounts'),
  enabledFlows: () => t('Enabled flows'),
  tasks: () => t('Tasks this month'),
  mcpExecutions: () => t('MCP this month'),
};

const formatValue = (kpi: OverviewKpi): string =>
  kpi.unit === 'percent'
    ? t('{value}%', { value: kpi.value })
    : formatUtils.formatNumber(kpi.value);

const withSign = (value: number): string =>
  value > 0 ? `+${value}` : `${value}`;

const changeClassName = (value: number): string => {
  if (value === 0) {
    return 'text-xs text-muted-foreground';
  }
  if (value > 0) {
    // The theme's success green is too dark to read on the dark card.
    return 'text-xs font-medium text-success dark:text-green-400';
  }
  return 'text-xs font-medium text-destructive';
};

const ChangeBadge = ({ change }: { change: OverviewKpi['change'] }) => {
  if (change === null) {
    return null;
  }
  const text =
    change.kind === 'points'
      ? t('{change} pts vs last month', { change: withSign(change.value) })
      : t('{change}% vs last month', { change: withSign(change.value) });

  return <span className={changeClassName(change.value)}>{text}</span>;
};

const capitalize = (word: string): string =>
  word.charAt(0).toUpperCase() + word.slice(1);

// "272 Business · 140 Smart": tiers are product names, read as-is from the
// indicators table, largest first.
const formatTierBreakdown = (byTier: Record<string, number>): string =>
  Object.entries(byTier)
    .sort(([, left], [, right]) => right - left)
    .map(
      ([tier, count]) =>
        `${formatUtils.formatNumber(count)} ${capitalize(tier)}`,
    )
    .join(' · ');

export const KpiStrip = ({
  accounts,
}: {
  accounts: AdminAccountsOverview | undefined;
}) => {
  if (!accounts) {
    return <Skeleton className="h-24 w-full" />;
  }
  const byTier = accounts.series.at(-1)?.provisionedAccountsByTier ?? {};

  return (
    <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
      {buildOverviewKpis(accounts).map((kpi) => (
        <Card key={kpi.key} className="p-4 flex flex-col gap-1.5">
          <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
            {KPI_LABELS[kpi.key]()}
          </span>
          <span className="text-2xl font-medium tabular-nums tracking-tight">
            {formatValue(kpi)}
          </span>
          <ChangeBadge change={kpi.change} />
          {kpi.key === 'provisionedAccounts' && (
            <span className="text-xs text-muted-foreground">
              {formatTierBreakdown(byTier)}
            </span>
          )}
        </Card>
      ))}
    </div>
  );
};
